import React from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

/** Dos vías: agente (propuestas) vs AI Designs (siluetas + nuevos diseños = mismo flujo). */
const TYPES = [
  { id: "propuestas", accent: "#4338ca" },
  { id: "nuevos_disenos", accent: "#0f766e" },
];

function isAiDesignsTipo(tipo) {
  return tipo === "siluetas" || tipo === "nuevos_disenos";
}

const pop = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const draw = keyframes`
  0%, 6% { stroke-dashoffset: 220; }
  42%, 78% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 0; }
`;

const fan = keyframes`
  0%, 12% { transform: translateX(-50%) rotate(0deg); }
  38%, 78% { transform: translateX(-50%) rotate(var(--fan)); }
  100% { transform: translateX(-50%) rotate(0deg); }
`;

const wash = keyframes`
  0%, 10% { opacity: 0; transform: scale(0.7); }
  28%, 78% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; }
`;

const pin = keyframes`
  0%, 28% { opacity: 0; transform: translateY(-8px); }
  42%, 80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; }
`;

const write = keyframes`
  0%, 12% { width: 0; }
  46%, 80% { width: 72%; }
  100% { width: 0; }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const JACKET =
  "M10 16 L16 8 H21 L24 13 L27 8 H32 L38 16 L44 24 L36 27 V50 H12 V27 L4 24 Z";

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 86,
        overflow: "hidden",
        bgcolor: tint,
        lineHeight: 0,
        fontSize: 0,
      }}
    >
      {children}
    </Box>
  );
}

/** Fila de iconos anclada al mismo eje vertical en ambas tarjetas. */
function PreviewIcons({ children, gap = 1 }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        top: 14,
        height: 58,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap,
        px: 1.25,
        boxSizing: "border-box",
      }}
    >
      {children}
    </Box>
  );
}

function PropuestasPreview() {
  const tones = ["#1e293b", "#44403c", "#0f766e"];
  return (
    <Stage tint="#f3efe8">
      <PreviewIcons gap={0.75}>
        {tones.map((tone, i) => (
          <Box
            key={tone}
            sx={{
              width: 40,
              height: 58,
              borderRadius: 1,
              bgcolor: "#fff",
              border: "1px solid #e6dfd4",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              animation: `${pop} 2.4s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
              ...motionSafe,
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 48 56"
              sx={{ width: 26, height: 32, display: "block" }}
            >
              <path d={JACKET} fill={tone} />
            </Box>
          </Box>
        ))}
      </PreviewIcons>
    </Stage>
  );
}

function SiluetasPreview() {
  return (
    <Stage tint="#f3efe8">
      <Box
        component="svg"
        viewBox="0 0 48 56"
        sx={{
          position: "absolute",
          left: "50%",
          top: 10,
          width: 52,
          height: 64,
          transform: "translateX(-50%)",
          "& path": {
            animation: `${draw} 4.8s ease-in-out infinite`,
            ...motionSafe,
          },
        }}
      >
        <path
          d={JACKET}
          fill="none"
          stroke="#0f766e"
          strokeWidth="1.7"
          strokeLinejoin="round"
          strokeDasharray="220"
        />
      </Box>
    </Stage>
  );
}

/** Siluetas + Nuevos diseños juntos (misma tarjeta). */
function SiluetasNuevosPreview() {
  return (
    <Stage tint="#f3efe8">
      <PreviewIcons gap={1.25}>
        <Box
          component="svg"
          viewBox="0 0 48 56"
          sx={{
            width: 46,
            height: 58,
            flexShrink: 0,
            display: "block",
            "& path": {
              animation: `${draw} 4.8s ease-in-out infinite`,
              ...motionSafe,
            },
          }}
        >
          <path
            d={JACKET}
            fill="none"
            stroke="#0f766e"
            strokeWidth="1.7"
            strokeLinejoin="round"
            strokeDasharray="220"
          />
        </Box>
        <Box
          sx={{
            width: 46,
            height: 52,
            flexShrink: 0,
            borderRadius: 1.5,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: alpha("#6a1b9a", 0.28),
            boxShadow: `0 6px 16px ${alpha("#6a1b9a", 0.12)}`,
            overflow: "hidden",
            "&:before": {
              content: '""',
              display: "block",
              height: 18,
              m: 0.65,
              borderRadius: 1,
              background: `linear-gradient(135deg, ${alpha("#6a1b9a", 0.55)}, ${alpha("#1565c0", 0.35)})`,
            },
            "&:after": {
              content: '""',
              display: "block",
              height: 5,
              mx: 1,
              mt: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#6a1b9a", 0.35),
              animation: `${write} 4.8s ease-in-out infinite`,
              ...motionSafe,
            },
          }}
        />
      </PreviewIcons>
    </Stage>
  );
}

function PresentacionPreview() {
  const fans = ["-14deg", "0deg", "14deg"];
  return (
    <Stage tint="#f6f1ea">
      {fans.map((angle) => (
        <Box
          key={angle}
          sx={{
            "--fan": angle,
            position: "absolute",
            left: "50%",
            top: 12,
            width: 42,
            height: 58,
            borderRadius: 0.6,
            bgcolor: "#fff",
            border: "1px solid #e4d9cc",
            boxShadow: "0 4px 10px rgba(80, 50, 20, 0.08)",
            transform: "translateX(-50%)",
            transformOrigin: "50% 90%",
            animation: `${fan} 4.8s ease-in-out infinite`,
            ...motionSafe,
            "&:before": {
              content: '""',
              display: "block",
              height: 18,
              m: 0.6,
              borderRadius: 0.4,
              bgcolor: "#9a3412",
              opacity: 0.85,
            },
          }}
        />
      ))}
    </Stage>
  );
}

function MoodPreview() {
  const chips = ["#1c1917", "#a16207", "#d6d3d1", "#0f766e"];
  return (
    <Stage tint="#f7f3ec">
      <Stack
        direction="row"
        spacing={0.8}
        sx={{ position: "absolute", left: 16, top: 22 }}
      >
        {chips.map((color, i) => (
          <Box
            key={color}
            sx={{
              width: 28,
              height: 40,
              borderRadius: 1,
              bgcolor: color,
              animation: `${wash} 4.8s ease-in-out infinite`,
              animationDelay: `${i * 0.12}s`,
              ...motionSafe,
            }}
          />
        ))}
      </Stack>
    </Stage>
  );
}

function RevisionPreview() {
  return (
    <Stage tint="#f3efe8">
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 12,
          width: 48,
          height: 62,
          transform: "translateX(-50%)",
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1px solid #e6dfd4",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box component="svg" viewBox="0 0 48 56" sx={{ width: 30, height: 38 }}>
          <path d={JACKET} fill="#1e293b" />
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 4,
            width: 14,
            height: 14,
            borderRadius: "50%",
            bgcolor: "#0369a1",
            border: "2px solid #fff",
            animation: `${pin} 4.8s ease-in-out infinite`,
            ...motionSafe,
          }}
        />
      </Box>
    </Stage>
  );
}

const PREVIEWS = {
  propuestas: PropuestasPreview,
  siluetas: SiluetasPreview,
  nuevos_disenos: SiluetasNuevosPreview,
  // Legacy previews (tareas antiguas en detalle)
  presentacion: PresentacionPreview,
  mood: MoodPreview,
  revision: RevisionPreview,
};

function TypeCard({ id, accent, selected, onSelect }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const Preview = PREVIEWS[id];
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        p: 0,
        m: 0,
        textAlign: "left",
        cursor: "pointer",
        borderRadius: 2.5,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? accent : "divider",
        bgcolor: "background.paper",
        boxShadow: selected ? `0 8px 22px ${alpha(accent, 0.16)}` : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        font: "inherit",
        color: "inherit",
        WebkitAppearance: "none",
        appearance: "none",
        "&:hover": { borderColor: selected ? accent : alpha(accent, 0.45) },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ position: "relative", lineHeight: 0, flexShrink: 0 }}>
        <Preview />
        {selected ? (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              color: accent,
              bgcolor: "#fff",
              borderRadius: "50%",
              fontSize: 20,
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ px: 1.25, py: 1.1, lineHeight: 1.35 }}>
        <Typography fontWeight={800} fontSize="0.92rem">
          {t(`skillAgent.taskType.${id}`)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
        >
          {t(`skillAgent.dialog.taskBody.${id}`)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function SkillAgentDesignPickDialog({
  open,
  onClose,
  value,
  onChange,
  fichas = [],
  searches = [],
  error,
  onClearError,
  submitting,
  onSubmit,
}) {
  const { t } = useTranslation();
  const ficha = fichas.find(
    (f) => String(f.id) === String(value?.SkillFichaId),
  );
  const lockClient = Boolean(value?.lockClient);
  const lockSeason = Boolean(value?.lockSeason);
  const clientLabel =
    value?.CustomerName || ficha?.Titulo || ficha?.SkillSlug || "";
  const branch =
    [clientLabel, value?.Temporada].filter(Boolean).join(" · ") ||
    t("skillAgent.common.emDash");
  const fromProject = lockClient || lockSeason;

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose?.()}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          mx: 2,
          width: "100%",
          maxWidth: 560,
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {t("skillAgent.dialog.newTaskTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {fromProject
            ? t("skillAgent.dialog.newTaskLeadFromProject", {
                defaultValue:
                  "Elige el tipo y pon un título. Cliente y temporada vienen del proyecto.",
              })
            : t("skillAgent.dialog.newTaskLead")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1.25 }}>
          {error ? (
            <Alert severity="error" onClose={onClearError}>
              {error}
            </Alert>
          ) : null}
          <Box
            sx={{
              px: 1.5,
              py: 1.1,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.primary.main, 0.06),
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              {fromProject
                ? t("skillAgent.form.lockedFromProject")
                : t("skillAgent.dialog.treeBranchLabel")}
            </Typography>
            <Typography variant="body1" fontWeight={800}>
              {branch}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 1.25,
              width: "100%",
            }}
          >
            {TYPES.map((type) => (
              <TypeCard
                key={type.id}
                id={type.id}
                accent={type.accent}
                selected={
                  value?.TipoTarea === type.id ||
                  (type.id === "nuevos_disenos" &&
                    isAiDesignsTipo(value?.TipoTarea))
                }
                onSelect={() =>
                  onChange?.((prev) => ({ ...prev, TipoTarea: type.id }))
                }
              />
            ))}
          </Box>
          {!lockClient ? (
            <FormControl size="small" fullWidth sx={pageFieldSx}>
              <InputLabel>{t("skillAgent.form.clientFicha")}</InputLabel>
              <Select
                label={t("skillAgent.form.clientFicha")}
                value={value?.SkillFichaId || ""}
                onChange={(e) =>
                  onChange?.((prev) => ({
                    ...prev,
                    SkillFichaId: e.target.value,
                  }))
                }
              >
                <MenuItem value="">{t("skillAgent.common.choose")}</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <TextField
              size="small"
              label={t("skillAgent.form.title")}
              placeholder={t("skillAgent.form.titlePlaceholder")}
              value={value?.Titulo || ""}
              onChange={(e) =>
                onChange?.((prev) => ({ ...prev, Titulo: e.target.value }))
              }
              fullWidth
              autoFocus
              sx={pageFieldSx}
            />
            {!lockSeason ? (
              <TextField
                size="small"
                label={t("skillAgent.form.season")}
                placeholder={t("skillAgent.form.seasonPlaceholder")}
                value={value?.Temporada || ""}
                onChange={(e) =>
                  onChange?.((prev) => ({ ...prev, Temporada: e.target.value }))
                }
                sx={{ width: { sm: 120 }, ...pageFieldSx }}
              />
            ) : null}
          </Stack>
          <TextField
            size="small"
            label={t("skillAgent.form.brief")}
            helperText={
              isAiDesignsTipo(value?.TipoTarea)
                ? t("skillAgent.form.briefHelperAiDesigns", {
                    defaultValue:
                      "Opcional: prompt inicial en AI Designs. Queda en Sketch / New designs.",
                  })
                : t("skillAgent.form.briefHelper")
            }
            multiline
            minRows={2}
            value={value?.Brief || ""}
            onChange={(e) =>
              onChange?.((prev) => ({ ...prev, Brief: e.target.value }))
            }
            fullWidth
            sx={pageFieldSx}
          />
          {value?.TipoTarea === "propuestas" ? (
            <FormControl size="small" fullWidth sx={pageFieldSx}>
              <InputLabel>{t("skillAgent.form.useSearchOptional")}</InputLabel>
              <Select
                label={t("skillAgent.form.useSearchOptional")}
                value={value?.searchId || ""}
                onChange={(e) =>
                  onChange?.((prev) => ({
                    ...prev,
                    searchId: e.target.value,
                  }))
                }
              >
                <MenuItem value="">
                  {t("skillAgent.form.noSearchRadarOnRun")}
                </MenuItem>
                {searches.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.Titulo}
                    {s.Temporada ? ` · ${s.Temporada}` : ""}
                  </MenuItem>
                ))}
              </Select>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.75, px: 0.25, lineHeight: 1.35 }}
              >
                {t("skillAgent.form.useSearchHelper")}
              </Typography>
            </FormControl>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t("skillAgent.common.cancel")}
        </Button>
        <Button
          variant="contained"
          sx={pagePrimaryButtonSx}
          disabled={
            submitting ||
            !value?.TipoTarea ||
            !String(value?.Titulo || "").trim()
          }
          onClick={onSubmit}
        >
          {submitting
            ? t("skillAgent.common.creating")
            : isAiDesignsTipo(value?.TipoTarea)
              ? t("skillAgent.dialog.openAiDesigns", {
                  defaultValue: "Abrir AI Designs",
                })
              : t("skillAgent.common.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
